import Command from "components/DesignSystem/Command/Command";
import Modal from "components/DesignSystem/Modal/Modal";
import { ADMIN_QUICK_NAV_PAGES } from "constants/crmPages";
import { useDeviceInfo } from "hooks/useDeviceInfo";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { GroupResponse } from "types/Models/groups";
import { getCrmPageLink } from "util/getCrmPageLink";
import { useDebounce } from "utils/debounce";
import { CompanyPages } from "./CompanyPages";
import { CompanyResults } from "./CompanyResults";
import {
  BACKSPACE_KEY,
  MAX_RECENT_COMPANIES,
  RECENT_COMPANIES_STORAGE_KEY,
} from "./constants";
import { ENTER_KEY, KEY_LABEL } from "components/CommandPalette/constants";
import { useCommandPaletteShortcut } from "components/CommandPalette/useCommandPaletteShortcut";

const EMPTY_RECENT_COMPANIES: GroupResponse[] = [];

export const CompanySearchModal = () => {
  const history = useHistory();
  const { isMac } = useDeviceInfo();

  const [isOpen, setIsOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [pageSearch, setPageSearch] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<GroupResponse | null>(
    null
  );
  const [activeItem, setActiveItem] = useState("");

  const companySearchTerm = useDebounce(companySearch);
  const { localData: recentCompanies, setItem: setRecentCompanies } =
    useLocalStorage<GroupResponse[]>(
      RECENT_COMPANIES_STORAGE_KEY,
      EMPTY_RECENT_COMPANIES
    );

  useCommandPaletteShortcut(() => setIsOpen(true));

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    setCompanySearch("");
    setPageSearch("");
    setSelectedCompany(null);
  };

  const handleSelectCompany = (company: GroupResponse) => {
    const otherRecent = recentCompanies?.filter(
      ({ uuid }) => uuid !== company.uuid
    );
    setRecentCompanies(
      [company, ...otherRecent].slice(0, MAX_RECENT_COMPANIES)
    );
    setSelectedCompany(company);
    setPageSearch("");
  };

  const handleBackToCompanies = () => setSelectedCompany(null);

  const handleRemoveRecentCompany = (companyId: string) =>
    setRecentCompanies(
      recentCompanies?.filter(({ uuid }) => uuid !== companyId)
    );

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey;

    if (!isModifiedClick) {
      handleOpenChange(false);
    }
  };

  const handleNavigate = (to: string) => {
    history.push(to);
    handleOpenChange(false);
  };

  const openActivePageInNewTab = (company: GroupResponse) => {
    const activePage = ADMIN_QUICK_NAV_PAGES.find(
      ({ name }) => name === activeItem
    );

    if (activePage) {
      window.open(getCrmPageLink(activePage, company.uuid), "_blank");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!selectedCompany) return;

    const isNewTabEnter = e.key === ENTER_KEY && (e.metaKey || e.ctrlKey);
    const isBackToCompanies = e.key === BACKSPACE_KEY && pageSearch === "";

    if (isNewTabEnter) {
      e.preventDefault();
      openActivePageInNewTab(selectedCompany);
    }

    if (isBackToCompanies) {
      e.preventDefault();
      handleBackToCompanies();
    }
  };

  const searchValue = selectedCompany ? pageSearch : companySearch;
  const handleSearchChange = selectedCompany ? setPageSearch : setCompanySearch;
  const placeholder = selectedCompany
    ? `Go to page in ${selectedCompany.name}`
    : "Search company";
  const modifierKey = isMac ? KEY_LABEL.MAC_MODIFIER : KEY_LABEL.MODIFIER;

  let footerShortcuts = (
    <Command.Shortcut keys={[KEY_LABEL.ENTER]}>Select company</Command.Shortcut>
  );

  if (selectedCompany) {
    footerShortcuts = (
      <>
        <Command.Shortcut keys={[KEY_LABEL.ENTER]}>Open page</Command.Shortcut>
        <Command.Shortcut keys={[modifierKey, KEY_LABEL.ENTER]}>
          Open in new tab
        </Command.Shortcut>
        <Command.Shortcut keys={[KEY_LABEL.BACKSPACE]}>
          Back to companies
        </Command.Shortcut>
      </>
    );
  }

  return (
    <Modal.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Modal.Content size="large" useCustomOverlay aria-describedby={undefined}>
        <Modal.Title className="t-sr-only">Search companies</Modal.Title>
        <Command.Root
          shouldFilter={Boolean(selectedCompany)}
          value={activeItem}
          onValueChange={setActiveItem}
          onKeyDown={handleKeyDown}
          loop
        >
          <Command.SearchInput
            key={selectedCompany?.uuid}
            autoFocus
            value={searchValue}
            onValueChange={handleSearchChange}
            placeholder={placeholder}
          />

          {selectedCompany ? (
            <CompanyPages
              company={selectedCompany}
              onNavigate={handleNavigate}
              onLinkClick={handleLinkClick}
              onBack={handleBackToCompanies}
            />
          ) : (
            <CompanyResults
              searchTerm={companySearchTerm}
              recentCompanies={recentCompanies}
              onSelectCompany={handleSelectCompany}
              onRemoveRecentCompany={handleRemoveRecentCompany}
            />
          )}

          <Command.Footer>
            <Command.Shortcut keys={[KEY_LABEL.UP, KEY_LABEL.DOWN]}>
              Navigate
            </Command.Shortcut>
            {footerShortcuts}
          </Command.Footer>
        </Command.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
